import { useEffect, useRef, useState } from 'react';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          'auto-rotate-delay'?: string;
          'auto-rotate'?: string;
          'rotation-per-second'?: string;
          'camera-controls'?: string;
          'disable-pan'?: string;
          'disable-zoom'?: string;
          'interaction-prompt'?: string;
          autoplay?: string;
          'animation-name'?: string;
          'shadow-intensity'?: string;
          'environment-image'?: string;
          'camera-orbit'?: string;
          exposure?: string;
          poster?: string;
          loading?: string;
          reveal?: string;
        },
        HTMLElement
      >;
    }
  }
}

interface ModelViewerProps {
  src: string;
  alt?: string;
  className?: string;
  autoRotate?: boolean;
  cameraControls?: boolean;
  disableZoom?: boolean;
  autoPlay?: boolean;
  pauseOnFirstFrame?: boolean;
  animationName?: string;
  shadowIntensity?: string;
  exposure?: string;
  cameraOrbit?: string;
  interactionPrompt?: string;
  eager?: boolean;
  onLoad?: () => void;
  onModelRef?: (el: HTMLElement | null) => void;
}

export function ModelViewer({
  src,
  alt = '3D Model',
  className = '',
  autoRotate = false,
  cameraControls = false,
  disableZoom = true,
  autoPlay = true,
  pauseOnFirstFrame = false,
  animationName,
  shadowIntensity = '0',
  exposure = '1',
  cameraOrbit,
  interactionPrompt,
  eager = false,
  onLoad,
  onModelRef,
}: ModelViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(eager);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    if (!document.querySelector('script[src*="model-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
      document.head.appendChild(script);
    }
  }, [isVisible]);

  useEffect(() => {
    if (eager) { setIsVisible(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '100px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [eager]);

  const initializedRef = useRef(false);
  const onModelRefRef = useRef(onModelRef);
  onModelRefRef.current = onModelRef;
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    if (!isVisible || initializedRef.current) return;
    const checkAndListen = () => {
      const modelViewer = containerRef.current?.querySelector('model-viewer');
      if (modelViewer) {
        const handleLoad = async () => {
          initializedRef.current = true;
          setIsLoaded(true);
          onLoadRef.current?.();
          if (pauseOnFirstFrame) {
            const mv = modelViewer as any;
            if (mv.updateComplete) await mv.updateComplete;
            mv.pause?.();
            mv.currentTime = 0;
            mv.removeAttribute('autoplay');
          }
          onModelRefRef.current?.(modelViewer as HTMLElement);
        };
        modelViewer.addEventListener('load', handleLoad);
        if ((modelViewer as any).loaded) {
          handleLoad();
        }
      }
    };
    const timeout = setTimeout(checkAndListen, 100);
    return () => clearTimeout(timeout);
  }, [isVisible, pauseOnFirstFrame]);

  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ minHeight: '200px' }}>
      {isVisible && (
        <model-viewer
          src={src}
          alt={alt}
          auto-rotate={autoRotate ? '' : undefined}
          camera-controls={cameraControls ? '' : undefined}
          disable-pan=""
          disable-zoom={disableZoom ? '' : undefined}
          autoplay={autoPlay ? '' : undefined}
          animation-name={animationName}
          shadow-intensity={shadowIntensity}
          interaction-prompt={interactionPrompt || 'none'}
          exposure={exposure}
          camera-orbit={cameraOrbit}
          loading="lazy"
          reveal="auto"
          style={{
            width: '100%',
            height: '100%',
            minHeight: '200px',
            backgroundColor: 'transparent',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease-out',
            display: 'block',
            ['--progress-bar-color' as any]: 'transparent',
            ['--progress-bar-height' as any]: '0px',
          }}
        />
      )}
      {(!isVisible || !isLoaded) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
